import React, { useState } from 'react';
import api from '../services/api';
import { useCustomerAuth } from '../hooks/useCustomerAuth';
import './CustomerLoginPopup.css';

const TRUSTED_DEVICE_STORAGE_KEY = 'customer_trusted_device_token';

export default function CustomerLoginPopup() {
  const { isLoggedIn, authLoading, dismissed, login, dismiss } = useCustomerAuth();

  // Steps: phone → otp → name (only for new users)
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (authLoading || isLoggedIn || dismissed) return null;

  /* Step 1 – try trusted-device login, otherwise send OTP via WhatsApp */
  const onSendCode = async () => {
    if (!phone.trim()) { setError('הזן מספר טלפון'); return; }
    setLoading(true);
    setError('');
    try {
      const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_STORAGE_KEY) || '';
      const payload = { phone: phone.trim() };
      if (trustedDeviceToken) payload.trusted_device_token = trustedDeviceToken;
      const res = await api.post('/customer/login', payload);
      const data = res.data || {};
      if (data.token) {
        if (rememberDevice && data.trusted_device_token) {
          localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, data.trusted_device_token);
        } else if (!rememberDevice) {
          localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY);
        }
        login(data.token, data.user);
      } else if (data.status === 'otp_sent' || data.status === 'throttled') {
        setStep('otp');
        if (data.status === 'throttled') setError(data.message || 'המתן מעט ונסה שוב');
      } else if (data.status === 'needs_name') {
        setStep('name');
      } else {
        setError(data.message || 'שגיאה');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'שגיאה בשליחת הקוד');
    } finally {
      setLoading(false);
    }
  };

  /* Step 2 – verify OTP */
  const onVerify = async () => {
    if (!code.trim()) { setError('הזן את הקוד'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/customer/login/verify', {
        phone: phone.trim(),
        code: code.trim(),
        remember_device: rememberDevice,
      });
      const data = res.data || {};
      if (data.token) {
        if (rememberDevice && data.trusted_device_token) {
          localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, data.trusted_device_token);
        } else if (!rememberDevice) {
          localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY);
        }
        // Returning user – done
        login(data.token, data.user);
      } else if (data.status === 'needs_name') {
        // First-time user – ask for name
        setStep('name');
      } else {
        setError('שגיאה באימות');
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'קוד לא נכון');
    } finally {
      setLoading(false);
    }
  };

  /* Step 3 – complete signup with name */
  const onComplete = async () => {
    if (!firstName.trim()) { setError('הזן שם פרטי'); return; }
    if (!lastName.trim()) { setError('הזן שם משפחה'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/customer/login/complete', {
        phone: phone.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        remember_device: rememberDevice,
      });
      const data = res.data || {};
      if (data.token) {
        if (rememberDevice && data.trusted_device_token) {
          localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, data.trusted_device_token);
        } else if (!rememberDevice) {
          localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY);
        }
        login(data.token, data.user);
      } else {
        setError(data.error || 'שגיאה בהשלמת הרשמה');
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה בהשלמת הרשמה');
    } finally {
      setLoading(false);
    }
  };

  const onResend = () => { setStep('phone'); setCode(''); setError(''); };

  return (
    <div className="clp-overlay" onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
      <div className="clp-popup" dir="rtl">
        <button className="clp-close" type="button" onClick={dismiss} aria-label="סגור">✕</button>

        <div className="clp-header">
          <span className="clp-emoji" aria-hidden>👋</span>
          <h2 className="clp-title">רוצה להתחבר?</h2>
          <p className="clp-subtitle">לקוחות מחוברים יכולים לראות היסטוריית הזמנות, להירשם לעסקים, לקבל תזכורות ולדרג עסקים!</p>
        </div>

        {error && <div className="clp-error">{error}</div>}

        {/* ── Step 1: phone number ── */}
        {step === 'phone' && (
          <div className="clp-form">
            <input
              className="clp-input clp-input--full"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="מספר טלפון (05X...)"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
            />
            <label className="clp-checkbox-row" htmlFor="clp-remember-device">
              <input
                id="clp-remember-device"
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              <span>שמור פרטים במכשיר הזה</span>
            </label>
            <button
              className="clp-btn clp-btn--primary"
              type="button"
              disabled={loading}
              onClick={onSendCode}
            >
              {loading ? 'מתחבר...' : 'התחבר'}
            </button>
          </div>
        )}

        {/* ── Step 2: OTP code ── */}
        {step === 'otp' && (
          <div className="clp-form">
            <p className="clp-otp-msg">שלחנו קוד אימות בוואטסאפ למספר {phone}</p>
            <input
              className="clp-input clp-input--full clp-input--otp"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="הזן קוד אימות"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
            />
            <button
              className="clp-btn clp-btn--primary"
              type="button"
              disabled={loading}
              onClick={onVerify}
            >
              {loading ? 'מאמת...' : 'אשר קוד'}
            </button>
            <button className="clp-btn clp-btn--link" type="button" onClick={onResend}>
              שלח קוד חדש
            </button>
          </div>
        )}

        {/* ── Step 3: first-time user name ── */}
        {step === 'name' && (
          <div className="clp-form">
            <p className="clp-otp-msg">נראה שזו הפעם הראשונה שלך 🎉 הזן את שמך:</p>
            <div className="clp-row">
              <input
                className="clp-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="שם פרטי"
                autoComplete="given-name"
                dir="rtl"
              />
              <input
                className="clp-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="שם משפחה"
                autoComplete="family-name"
                dir="rtl"
              />
            </div>
            <button
              className="clp-btn clp-btn--primary"
              type="button"
              disabled={loading}
              onClick={onComplete}
            >
              {loading ? 'שומר...' : 'סיום הרשמה'}
            </button>
          </div>
        )}

        <button
          className="clp-btn clp-btn--ghost"
          type="button"
          onClick={dismiss}
        >
          לא עכשיו
        </button>
      </div>
    </div>
  );
}
